/**
 * Cloudflare R2 同步客户端
 *
 * 中国大陆访问说明：
 * 1. 需要绑定自定义域名到 Cloudflare Worker
 * 2. 默认 workers.dev 域名在国内可能不稳定
 * 3. 客户端会自动检测连通性并降级
 */

import type { UserDataPackage, SyncResult, SyncState } from './types';
import { getDeviceId, setDeviceId, generateDeviceId } from '../utils/deviceId';

// 从统一模块重新导出，保持向后兼容
export { getDeviceId, setDeviceId, generateDeviceId };

// 同步 API 地址
// 部署到 Cloudflare Pages 后，API 在同一域名下：/api/sync
// 本地开发时可以设置 VITE_SYNC_API 指向其他地址
const SYNC_API_PRIMARY = import.meta.env.VITE_SYNC_API || '/api';
const SYNC_API_FALLBACK = import.meta.env.VITE_SYNC_API_FALLBACK || '';

const SYNC_STATE_KEY = 'enpass_sync_state';
const SYNC_VERSION_KEY = 'enpass_sync_version';
const SYNC_TIMEOUT = 15000; // 15秒超时（国内网络可能慢）

/** 获取当前已知的云端同步版本号 */
export function getCloudSyncVersion(): number {
  const stored = localStorage.getItem(SYNC_VERSION_KEY);
  return stored ? parseInt(stored, 10) : 0;
}

/** 保存云端同步版本号 */
function saveCloudSyncVersion(version: number): void {
  localStorage.setItem(SYNC_VERSION_KEY, version.toString());
}

/** 获取同步状态 */
export function getSyncState(): SyncState {
  const stored = localStorage.getItem(SYNC_STATE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // ignore
    }
  }
  return {
    lastSyncAt: null,
    lastSyncStatus: 'never',
    pendingChanges: 0,
    deviceId: getDeviceId(),
  };
}

/** 保存同步状态 */
function saveSyncState(state: SyncState): void {
  localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(state));
}

/** 检测 API 连通性 */
async function checkConnectivity(apiUrl: string): Promise<boolean> {
  if (!apiUrl) return false;
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const res = await fetch(`${apiUrl}/health`, {
      method: 'GET',
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    return res.ok;
  } catch {
    return false;
  }
}

/** 获取可用的 API 地址 */
async function getAvailableApi(): Promise<string | null> {
  // 优先尝试主 API
  if (await checkConnectivity(SYNC_API_PRIMARY)) {
    return SYNC_API_PRIMARY;
  }
  
  // 尝试备用 API
  if (await checkConnectivity(SYNC_API_FALLBACK)) {
    return SYNC_API_FALLBACK;
  }
  
  return null;
}

/** 上传数据到云端 */
export async function pushToCloud(data: UserDataPackage): Promise<SyncResult> {
  const apiUrl = await getAvailableApi();
  
  if (!apiUrl) {
    const state = getSyncState();
    state.lastSyncStatus = 'failed';
    state.pendingChanges++;
    saveSyncState(state);
    
    return {
      success: false,
      timestamp: Date.now(),
      error: '无法连接到同步服务器，请检查网络',
    };
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SYNC_TIMEOUT);

    const res = await fetch(`${apiUrl}/sync?deviceId=${data.deviceId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const state = getSyncState();
    state.lastSyncAt = Date.now();
    state.lastSyncStatus = 'success';
    state.pendingChanges = 0;
    saveSyncState(state);

    // 上传成功后更新本地版本号
    if (data.syncVersion) {
      saveCloudSyncVersion(data.syncVersion);
    }

    return {
      success: true,
      timestamp: Date.now(),
      uploaded: data.cards.length,
    };
  } catch (error) {
    const state = getSyncState();
    state.lastSyncStatus = 'failed';
    saveSyncState(state);

    return {
      success: false,
      timestamp: Date.now(),
      error: error instanceof Error ? error.message : '同步失败',
    };
  }
}

/** 从云端下载数据 */
export async function pullFromCloud(): Promise<{ success: boolean; data?: UserDataPackage; error?: string }> {
  const apiUrl = await getAvailableApi();
  const deviceId = getDeviceId();

  if (!apiUrl) {
    return {
      success: false,
      error: '无法连接到同步服务器',
    };
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SYNC_TIMEOUT);

    const res = await fetch(`${apiUrl}/sync?deviceId=${deviceId}`, {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const text = await res.text();
    if (!text || text === '{}' || text === 'null') {
      return { success: true, data: undefined };
    }

    const data = JSON.parse(text) as UserDataPackage;
    // 保存云端版本号
    if (data.syncVersion) {
      saveCloudSyncVersion(data.syncVersion);
    }
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : '下载失败',
    };
  }
}

/** 检查是否配置了同步 */
export function isSyncConfigured(): boolean {
  // 检查是否启用同步功能
  const enableSync = import.meta.env.VITE_ENABLE_SYNC !== 'false';
  if (!enableSync) return false;
  
  return !!(SYNC_API_PRIMARY || SYNC_API_FALLBACK);
}

/** 检查同步是否可用（用于 UI 显示） */
export async function checkSyncAvailable(): Promise<boolean> {
  const api = await getAvailableApi();
  return api !== null;
}

/** 删除云端数据 */
export async function deleteFromCloud(): Promise<SyncResult> {
  const apiUrl = await getAvailableApi();
  const deviceId = getDeviceId();

  if (!apiUrl) {
    return {
      success: false,
      timestamp: Date.now(),
      error: '无法连接到同步服务器',
    };
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SYNC_TIMEOUT);

    const res = await fetch(`${apiUrl}/sync?deviceId=${deviceId}`, {
      method: 'DELETE',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    // 重置同步状态
    const state = getSyncState();
    state.lastSyncAt = null;
    state.lastSyncStatus = 'never';
    state.pendingChanges = 0;
    saveSyncState(state);

    return {
      success: true,
      timestamp: Date.now(),
    };
  } catch (error) {
    return {
      success: false,
      timestamp: Date.now(),
      error: error instanceof Error ? error.message : '删除失败',
    };
  }
}
